import React from 'react';
import DodTermText from '../DodTermText';
import type { DodGlossaryTermId } from '../dodGlossary';
import {
  COACH_SCRIPTS,
  coachAnswer,
  coachAsk,
  coachDoStep,
  coachText,
  itemCoachMode,
  type CoachLanguage,
  type CoachTurn,
} from '../constants/complianceCoach';

type Props = {
  itemId: string;
  steps: string[];
  language: CoachLanguage;
  apiBase?: string;
  accessToken?: string;
  seenTerms?: Set<DodGlossaryTermId>;
  onUseAnswer?: (note: string) => void;
  defaultOpen?: boolean;
};

const CoachIcon: React.FC = () => (
  <svg className="compliance-coach-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M8 1.2l1.1 3.2 3.2 1.1-3.2 1.1L8 9.8 6.9 6.6 3.7 5.5l3.2-1.1L8 1.2zm4.2 7.1.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6.6-1.6z"
    />
  </svg>
);

const softenLine = async (
  line: string,
  apiBase: string | undefined,
  accessToken: string | undefined,
): Promise<string> => {
  const base = (apiBase || '').replace(/\/$/, '');
  if (!base || !accessToken || !line.trim()) {
    return line;
  }
  try {
    const response = await fetch(`${base}/api/v1/compliance-consoles/coach`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ line }),
    });
    if (!response.ok) {
      return line;
    }
    const body = await response.json();
    const next = typeof body?.line === 'string' ? body.line.trim() : '';
    return next || line;
  } catch {
    return line;
  }
};

const ComplianceCoach: React.FC<Props> = ({
  itemId,
  steps,
  language,
  apiBase,
  accessToken,
  seenTerms,
  onUseAnswer,
  defaultOpen = false,
}) => {
  const mode = itemCoachMode(itemId);
  const script = COACH_SCRIPTS[itemId];
  const es = language === 'es';
  const [open, setOpen] = React.useState(defaultOpen);
  const [turn, setTurn] = React.useState<CoachTurn | null>(script ? coachAsk(script, script.start) : null);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [why, setWhy] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const label = mode === 'decide'
    ? (es ? 'Ayúdame a decidir' : 'Help me decide')
    : (es ? 'Ayúdame a hacerlo' : 'Help me do it');

  React.useEffect(() => {
    setOpen(defaultOpen);
    setTurn(script ? coachAsk(script, script.start) : null);
    setStepIndex(0);
    setWhy('');
    setSaved(false);
  }, [defaultOpen, itemId, script]);

  React.useEffect(() => {
    if (!open || turn?.kind !== 'answer') {
      return undefined;
    }
    let cancelled = false;
    const source = coachText(turn.outcome.why, language);
    setWhy(source);
    void softenLine(source, apiBase, accessToken).then((line) => {
      if (!cancelled) {
        setWhy(line);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiBase, language, open, turn]);

  const step = coachDoStep(steps, stepIndex, language);
  if (mode === 'do' && !step) {
    return null;
  }
  if (mode === 'decide' && !script) {
    return null;
  }

  const saveAnswer = (note: string) => {
    onUseAnswer?.(note);
    setSaved(true);
  };

  return (
    <div className={`compliance-coach${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="compliance-coach-launch"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CoachIcon />
        {label}
      </button>
      {open && mode === 'decide' && turn?.kind === 'ask' ? (
        <div className="compliance-coach-panel" role="group" aria-label={label}>
          <p className="compliance-coach-count">
            {es
              ? `Pregunta ${turn.index + 1} de ${turn.total}`
              : `Question ${turn.index + 1} of ${turn.total}`}
          </p>
          <p className="compliance-coach-prompt">
            <DodTermText text={coachText(turn.ask.prompt, language)} language={language} seen={seenTerms} />
          </p>
          <div className="compliance-coach-choices">
            {turn.ask.choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="dashboard-btn"
                onClick={() => {
                  if (!script) {
                    return;
                  }
                  setSaved(false);
                  setTurn(coachAnswer(script, turn.ask.id, choice.id));
                }}
              >
                {coachText(choice.label, language)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {open && mode === 'decide' && turn?.kind === 'answer' ? (
        <div className="compliance-coach-panel" role="group" aria-label={label}>
          <p className="compliance-coach-write">
            {es ? 'Escriba esto: ' : 'Write this: '}
            <strong>
              <DodTermText text={coachText(turn.outcome.write, language)} language={language} seen={seenTerms} />
            </strong>
          </p>
          <p className="compliance-coach-why">{why}</p>
          <p className="compliance-coach-hint">
            {es
              ? 'Esto guarda la respuesta en el paso. No marca el paso como hecho.'
              : 'This saves the answer on the step. It does not mark the step done.'}
          </p>
          <div className="compliance-coach-choices">
            <button
              type="button"
              className="dashboard-btn"
              onClick={() => saveAnswer(coachText(turn.outcome.write, language))}
            >
              {saved ? (es ? 'Respuesta guardada' : 'Answer saved') : (es ? 'Usar esta respuesta' : 'Use this answer')}
            </button>
            <button
              type="button"
              className="dashboard-btn"
              onClick={() => {
                if (!script) {
                  return;
                }
                setSaved(false);
                setTurn(coachAsk(script, script.start));
              }}
            >
              {es ? 'Preguntar de nuevo' : 'Ask again'}
            </button>
          </div>
        </div>
      ) : null}
      {open && mode === 'do' && step ? (
        <div className="compliance-coach-panel" role="group" aria-label={label}>
          <p className="compliance-coach-count">
            {es
              ? `Paso ${step.index + 1} de ${step.total}`
              : `Step ${step.index + 1} of ${step.total}`}
          </p>
          <p className="compliance-coach-prompt">
            <DodTermText text={step.text} language={language} seen={seenTerms} />
          </p>
          <p className="compliance-coach-why">
            {es ? 'Debe ver: ' : 'You should see: '}
            {step.see}
          </p>
          <div className="compliance-coach-choices">
            {step.index > 0 ? (
              <button type="button" className="dashboard-btn" onClick={() => setStepIndex(step.index - 1)}>
                {es ? 'Atrás' : 'Back'}
              </button>
            ) : null}
            {step.last ? (
              <button
                type="button"
                className="dashboard-btn"
                onClick={() => saveAnswer(es ? 'Seguí los pasos. Falta guardar la prueba.' : 'I followed the steps. The proof still needs to be saved.')}
              >
                {saved ? (es ? 'Nota guardada' : 'Note saved') : (es ? 'Terminé los pasos' : 'I finished the steps')}
              </button>
            ) : (
              <button type="button" className="dashboard-btn" onClick={() => setStepIndex(step.index + 1)}>
                {es ? 'Ya lo hice' : 'I did this'}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ComplianceCoach;
